import axios, { isAxiosError } from "axios";
import { Cookies } from "react-cookie";
import dayjs from "dayjs";

import { env } from "@monta/env";

import type { AxiosRequestConfig } from "axios";

// * Start - monolithicClient
export const monolithicClient = axios.create({
  baseURL: `${env.API_URL}/monolithic`
});

monolithicClient.interceptors.request.use((config) => {
  const controller = new AbortController();
  config.signal = controller.signal;

  const token = new Cookies().get("accessToken");

  if (!token) {
    controller.abort();
    return config;
  }

  config.headers.Authorization = token;
  return config;
});

monolithicClient.interceptors.response.use(null, async (error) => {
  if (!isAxiosError(error)) return Promise.reject(error);

  const cookies = new Cookies();
  const refresh = cookies.get("refreshToken");

  if (!refresh) window.location.reload();

  if (
    (error.response?.status === 403 || error.response?.status === 401) &&
    refresh
  ) {
    const response = await fetch(`${env.API_URL}/auth/refresh`, {
      method: "POST",
      body: JSON.stringify({ token: refresh })
    });

    const data = (await response.json()) as {
      accessToken: string;
      refreshToken: string;
    };

    cookies.set("accessToken", data.accessToken, {
      path: "/",
      domain: env.COOKIE_DOMAIN,
      expires: dayjs().add(2, "weeks").toDate(),
      secure: true,
      sameSite: "none"
    });

    cookies.set("refreshToken", data.refreshToken, {
      path: "/",
      domain: env.COOKIE_DOMAIN,
      expires: dayjs().add(2, "weeks").toDate(),
      secure: true,
      sameSite: "none"
    });

    const axiosConfig = error.config as AxiosRequestConfig;

    return monolithicClient(axiosConfig);
  }
});
// * End - monolithicClient

// * Start - gwtClient
export const gwtClient = axios.create({
  baseURL: `${env.API_URL}/`
});

gwtClient.interceptors.request.use((config) => {
  const controller = new AbortController();
  config.signal = controller.signal;

  const token = new Cookies().get("accessToken");

  if (!token) {
    controller.abort();
    return config;
  }

  config.headers.Authorization = token;
  // parameter to set for changing content type to default
  if (!config.fetchOptions?.["isJson"])
    config.headers["Content-Type"] =
      "application/x-www-form-urlencoded; charset=UTF-8";
  return config;
});

gwtClient.interceptors.response.use(null, async (error) => {
  if (!isAxiosError(error)) return Promise.reject(error);

  const cookies = new Cookies();
  const refresh = cookies.get("refreshToken");

  if (!refresh) window.location.reload();

  if (
    (error.response?.status === 403 || error.response?.status === 401) &&
    refresh
  ) {
    const response = await fetch(`${env.API_URL}/auth/refresh`, {
      method: "POST",
      body: JSON.stringify({ token: refresh })
    });

    const data = (await response.json()) as {
      accessToken: string;
      refreshToken: string;
    };

    cookies.set("accessToken", data.accessToken, {
      path: "/",
      domain: env.COOKIE_DOMAIN,
      expires: dayjs().add(2, "weeks").toDate(),
      secure: true,
      sameSite: "none"
    });

    cookies.set("refreshToken", data.refreshToken, {
      path: "/",
      domain: env.COOKIE_DOMAIN,
      expires: dayjs().add(2, "weeks").toDate(),
      secure: true,
      sameSite: "none"
    });

    const axiosConfig = error.config as AxiosRequestConfig;

    return gwtClient(axiosConfig);
  }
});
// * End - gwtClient

// * Start - nodeClient
export const nodeClient = axios.create({
  baseURL: `${env.LEGACY_APPLICATION_URL}/api`
});

nodeClient.interceptors.request.use((config) => {
  const controller = new AbortController();
  config.signal = controller.signal;

  const token = new Cookies().get("accessToken");

  if (!token) {
    controller.abort();
    return config;
  }

  config.headers.Authorization = token;
  config.headers.Accept = "application/pdf";
  return config;
});

nodeClient.interceptors.response.use(null, async (error) => {
  if (!isAxiosError(error)) return Promise.reject(error);

  const cookies = new Cookies();
  const refresh = cookies.get("refreshToken");

  if (!refresh) window.location.reload();

  if (
    (error.response?.status === 403 || error.response?.status === 401) &&
    refresh
  ) {
    const response = await fetch(`${env.API_URL}/auth/refresh`, {
      method: "POST",
      body: JSON.stringify({ token: refresh })
    });

    const data = (await response.json()) as {
      accessToken: string;
      refreshToken: string;
    };

    cookies.set("accessToken", data.accessToken, {
      path: "/",
      domain: env.COOKIE_DOMAIN,
      expires: dayjs().add(2, "weeks").toDate(),
      secure: true,
      sameSite: "none"
    });

    cookies.set("refreshToken", data.refreshToken, {
      path: "/",
      domain: env.COOKIE_DOMAIN,
      expires: dayjs().add(2, "weeks").toDate(),
      secure: true,
      sameSite: "none"
    });

    const axiosConfig = error.config as AxiosRequestConfig;

    return nodeClient(axiosConfig);
  }
});
// * End - nodeClient
